import { defineStore } from 'pinia'
import {computed, ref, watch} from 'vue'
import { Customer } from '@js/api/types/customer/Customer'

export const useUserStore = defineStore('user', () => {

  /**
   * Reads and parses the stored Customer profile from localStorage.
   *
   * @returns The parsed Customer object, or null if missing or invalid.
   */
  const loadUserDataFromLocalStorage = (): Customer | null => {
    const userData = localStorage.getItem('user')
    if (!userData) return null
    try {
      return JSON.parse(userData) as Customer
    } catch (e) {
      console.error('Failed to parse user data from localStorage:', e)
      return null
    }
  }

  /** Global reactive state holding the active user details. */
  const user = ref<Customer | null>(loadUserDataFromLocalStorage())

  /**
   * Synchronizes changes in the global user state back to localStorage.
   * Performs a deep watch to detect updates to nested properties (e.g., incompleteRegistrationCount).
   */
  watch(
    user,
    (newUser) => {
      if (newUser) {
        localStorage.setItem('user', JSON.stringify(newUser))
      } else {
        localStorage.removeItem('user')
      }
    },
    { deep: true },
  )

  /**
   * Reactive getter for incomplete registrations count.
   */
  const incompleteRegistrationCount = computed<number>(() => {
    return user.value?.incompleteRegistrationCount ?? 0
  })

  /**
   * Increments the incomplete registrations count for the user.
   *
   * @param amount - The number to add to the current count (defaults to 1)
   */
  function incrementIncompleteRegistrationsCount(amount = 1): void {
    if (user.value) {
      user.value.incompleteRegistrationCount += amount
    }
  }

  /**
   * Decrements the incomplete registrations count for the user, ensuring it never drops below 0.
   *
   * @param amount - The number to subtract from the current count (defaults to 1)
   */
  function decrementIncompleteRegistrationsCount(amount = 1): void {
    if (user.value) {
      user.value.incompleteRegistrationCount = Math.max(
        0,
        user.value.incompleteRegistrationCount - amount,
      )
    }
  }

  return {
    user,
    incompleteRegistrationCount,
    incrementIncompleteRegistrationsCount,
    decrementIncompleteRegistrationsCount,
  }
})
